/**
 * modals/FeeAssignmentModal.tsx
 * Fee assignment modal for configuring commission, overnight financing, and currency conversion fees
 * 
 * PHASE 2: Modal shell with proper layouts and user flow validation
 * Form fields present with HeroUI validation styling, save/cancel buttons (non-functional)
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { DollarSign, Clock, CreditCard } from 'lucide-react';

// 2. Internal absolute imports
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Select, SelectItem, Switch, Card, CardBody, Alert, useDisclosure } from '@enfomentis/ui';
import { CurrencyChooserModal } from '@/components/shared/CurrencyChooser/CurrencyChooserModal';
import type { DisplayFeeConfig } from '../hooks/useFeeHierarchy';

/**
 * Props for the FeeAssignmentModal component
 */
interface FeeAssignmentModalProps {
  /** Whether the modal is open */
  isOpen: boolean;

  /** Handler for closing the modal */
  onClose: () => void;

  /** Title for the modal (e.g., "Assign Asset Class Fees", "Assign Instrument Fees") */
  title: string;

  /** Optional subtitle for context */
  subtitle?: string;

  /** Existing fee configuration for editing */
  existingFees?: DisplayFeeConfig;

  /** Handler for saving fee configuration (Phase 2: non-functional) */
  onSave: (feeData: FeeFormData) => void;
}

/**
 * Form data structure for fee configuration
 */
interface FeeFormData {
  // Commission configuration
  hasCommission: boolean;
  commissionType: 'fixed' | 'percentage' | 'perContract' | 'tiered';
  commissionValue: string;
  commissionCurrency: string;
  commissionStructure: 'perSide' | 'roundTrip';
  commissionMinimum?: string;
  
  // Overnight financing configuration  
  hasOvernightFinancing: boolean;
  overnightBenchmark: string;
  overnightSpread: string;
  overnightFrequency: 'daily' | 'weekends' | 'holidays';
  
  // Currency conversion configuration
  hasCurrencyConversion: boolean;
  conversionRateType: 'spot' | 'interbank' | 'spotPlusMarkup';
  conversionMarkup: string;
  
  // Additional notes
  notes: string;
}

/**
 * Initial form data
 */
const initialFormData: FeeFormData = {
  hasCommission: true,
  commissionType: 'fixed',
  commissionValue: '',
  commissionCurrency: 'USD',
  commissionStructure: 'perSide',
  commissionMinimum: '',
  
  hasOvernightFinancing: false,
  overnightBenchmark: 'SOFR',
  overnightSpread: '',
  overnightFrequency: 'daily',
  
  hasCurrencyConversion: false,
  conversionRateType: 'spotPlusMarkup',
  conversionMarkup: '',
  
  notes: ''
};

/**
 * Commission type options
 */
const commissionTypeOptions = [
  { key: 'fixed', label: 'Fixed Amount' },
  { key: 'percentage', label: 'Percentage of Trade Value' },
  { key: 'perContract', label: 'Per Contract/Share' },
  { key: 'tiered', label: 'Tiered Structure' }
];

/**
 * Benchmark rate options for overnight financing
 */
const benchmarkOptions = [
  { key: 'SOFR', label: 'SOFR (Secured Overnight Financing Rate)' },
  { key: 'LIBOR', label: 'LIBOR (London Interbank Offered Rate)' },
  { key: 'Fed Funds', label: 'Federal Funds Rate' },
  { key: 'ECB', label: 'ECB Main Refinancing Rate' },
  { key: 'BOE', label: 'Bank of England Base Rate' },
  { key: 'Custom', label: 'Custom Benchmark' }
];

/**
 * Convert DisplayFeeConfig to FeeFormData for form initialization
 */
const displayFeesToFormData = (existingFees: DisplayFeeConfig): Partial<FeeFormData> => {
  const formData: Partial<FeeFormData> = {};

  // Commission fees
  if (existingFees.commission) {
    formData.hasCommission = true;

    // Extract amount from display format (e.g., "$5.50/contract/side")
    const amountMatch = existingFees.commission.match(/\$?(\d+(?:\.\d+)?)/);
    if (amountMatch?.[1]) {
      formData.commissionValue = amountMatch[1];
    }

    // Determine commission type from display format
    if (existingFees.commission.includes('/contract')) {
      formData.commissionType = 'perContract';
    } else if (existingFees.commission.includes('%')) {
      formData.commissionType = 'percentage';
    } else if (existingFees.commission.includes('Tiered')) {
      formData.commissionType = 'tiered';
    } else {
      formData.commissionType = 'fixed';
    }

    // Determine commission structure from display format
    if (existingFees.commission.includes('/round trip')) {
      formData.commissionStructure = 'roundTrip';
    } else {
      formData.commissionStructure = 'perSide';
    }
  }

  // Overnight financing
  if (existingFees.overnight) {
    formData.hasOvernightFinancing = true;

    // Parse overnight format like "SOFR + 2.5%" or "LIBOR - 1.0%"
    const overnightMatch = existingFees.overnight.match(/([A-Z]+)?\s*([+-]?)\s*(\d+(?:\.\d+)?)/);

    // Extract benchmark
    if (overnightMatch?.[1]) {
      formData.overnightBenchmark = overnightMatch[1];
    }

    // Extract spread value with sign
    if (overnightMatch?.[3]) {
      const sign = overnightMatch[2] === '-' ? '-' : '';
      formData.overnightSpread = `${sign}${overnightMatch[3]}`;
    }
  }

  // Currency conversion
  if (existingFees.conversion) {
    formData.hasCurrencyConversion = true;
    // Extract markup value if possible
    const markupMatch = existingFees.conversion.match(/(\d+(?:\.\d+)?)/);
    if (markupMatch?.[1]) {
      formData.conversionMarkup = markupMatch[1];
    }
  }

  // Notes
  if (existingFees.notes) {
    formData.notes = existingFees.notes;
  }

  return formData;
};

/**
 * FeeAssignmentModal component for configuring fees
 */
export const FeeAssignmentModal: React.FC<FeeAssignmentModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  existingFees,
  onSave
}) => {
  const [formData, setFormData] = useState<FeeFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form with existing fees when modal opens
  useEffect(() => {
    if (isOpen) {
      if (existingFees) {
        // Pre-populate with existing fees
        const existingFormData = displayFeesToFormData(existingFees);
        setFormData({ ...initialFormData, ...existingFormData });
      } else {
        // Reset to initial form data for new assignment
        setFormData(initialFormData);
      }
      setErrors({});
    }
  }, [isOpen, existingFees]);

  // Currency chooser modal state
  const { isOpen: isCurrencyChooserOpen, onOpen: onCurrencyChooserOpen, onClose: onCurrencyChooserClose } = useDisclosure();

  // Validation functions
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Check if at least one fee type is enabled
    if (!formData.hasCommission && !formData.hasOvernightFinancing && !formData.hasCurrencyConversion) {
      newErrors['general'] = 'At least one fee type must be configured';
    }
    
    // Commission validation
    if (formData.hasCommission) {
      if (!formData.commissionValue || formData.commissionValue.trim() === '') {
        newErrors['commissionValue'] = 'Commission amount is required';
      } else if (isNaN(Number(formData.commissionValue))) {
        newErrors['commissionValue'] = 'Commission amount must be a valid number';
      } else if (Number(formData.commissionValue) < 0) {
        newErrors['commissionValue'] = 'Commission amount cannot be negative';
      }
      
      if (formData.commissionType === 'tiered' && formData.commissionMinimum) {
        if (formData.commissionMinimum.trim() !== '' && isNaN(Number(formData.commissionMinimum))) {
          newErrors['commissionMinimum'] = 'Minimum commission must be a valid number';
        }
      }
    }
    
    // Overnight financing validation
    if (formData.hasOvernightFinancing) {
      if (!formData.overnightSpread || formData.overnightSpread.trim() === '') {
        newErrors['overnightSpread'] = 'Overnight financing spread is required';
      } else if (isNaN(Number(formData.overnightSpread))) {
        newErrors['overnightSpread'] = 'Overnight financing spread must be a valid number';
      }
    }
    
    // Currency conversion validation
    if (formData.hasCurrencyConversion) {
      if (!formData.conversionMarkup || formData.conversionMarkup.trim() === '') {
        newErrors['conversionMarkup'] = 'Currency conversion markup is required';
      } else if (isNaN(Number(formData.conversionMarkup))) {
        newErrors['conversionMarkup'] = 'Currency conversion markup must be a valid number';
      } else if (Number(formData.conversionMarkup) < 0) {
        newErrors['conversionMarkup'] = 'Currency conversion markup cannot be negative';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form field updates
  const updateFormData = (field: keyof FeeFormData, value: string | boolean): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
    
    // Clear general error when user makes changes
    if (errors['general'] && (field === 'hasCommission' || field === 'hasOvernightFinancing' || field === 'hasCurrencyConversion')) {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated['general'];
        return updated;
      });
    }
  };

  // Handle save action with validation
  const handleSave = (): void => {
    console.log('🚀 STEP 6: Fee assignment save clicked with validation', formData);
    
    // Validate form before saving
    if (!validateForm()) {
      console.log('🚀 STEP 6: Form validation failed', errors);
      return;
    }
    
    setIsSubmitting(true);
    console.log('🚀 STEP 6: Form validation passed, saving fee configuration');
    
    // Simulate async operation
    setTimeout(() => {
      onSave(formData);
      setIsSubmitting(false);
      onClose();
      setFormData(initialFormData); // Reset form
      setErrors({}); // Reset errors
    }, 1000);
  };

  // Handle cancel action
  const handleCancel = (): void => {
    setFormData(initialFormData); // Reset form
    setErrors({}); // Reset errors
    onClose();
  };

  // Handle currency selection
  const handleCurrencySelect = (currency: string): void => {
    updateFormData('commissionCurrency', currency);
    onCurrencyChooserClose();
  };

  // Get currency symbol for display
  const getCurrencySymbol = (currency: string): string => {
    const symbols: Record<string, string> = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CAD': 'C$',
      'AUD': 'A$',
      'CHF': 'CHF'
    };
    return symbols[currency] || currency;
  };

  // Commission section helper
  const getCommissionInputProps = () => {
    const currencySymbol = getCurrencySymbol(formData.commissionCurrency);
    
    switch (formData.commissionType) {
      case 'fixed':
        return {
          startContent: currencySymbol,
          placeholder: '4.95',
          step: '0.01', // Second decimal place for dollar amounts
          description: `Fixed commission amount per ${formData.commissionStructure === 'perSide' ? 'side' : 'round trip'}`
        };
      case 'percentage':
        return {
          endContent: '%',
          placeholder: '0.3',
          step: '0.1', // First decimal place for percentages
          description: 'Percentage of total trade value'
        };
      case 'perContract':
        return {
          startContent: currencySymbol,
          placeholder: '1.50',
          step: '0.01', // Second decimal place for dollar amounts
          description: `Commission per contract/share per ${formData.commissionStructure === 'perSide' ? 'side' : 'round trip'}`
        };
      case 'tiered':
        return {
          placeholder: 'See notes for details',
          description: 'Complex tiered structure - see notes section',
          isDisabled: false
        };
      default:
        return { 
          placeholder: '0.00',
          step: '0.01'
        };
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onOpenChange={onClose}
      size="lg"
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">{title}</h3>
              </div>
              {subtitle && (
                <p className="text-sm text-text-secondary font-normal">{subtitle}</p>
              )}
            </ModalHeader>

            <ModalBody className="gap-6">
              {/* Commission Configuration */}
              <Card radius="sm" shadow="none" className="border border-default-200 overflow-visible">
                <CardBody className="gap-4 overflow-visible">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-success" />
                      <h4 className="font-semibold">Commission Fees</h4>
                    </div>
                    <Switch
                      size="sm"
                      isSelected={formData.hasCommission}
                      onValueChange={(value) => updateFormData('hasCommission', value)}
                    />
                  </div>
                  
                  {formData.hasCommission && (
                    <div className="space-y-4">
                      <Select
                        label="Commission Type"
                        variant="underlined"
                        size="sm"
                        selectedKeys={[formData.commissionType]}
                        onSelectionChange={(keys) => 
                          updateFormData('commissionType', Array.from(keys)[0] as string)
                        }
                        classNames={{
                          listboxWrapper: "data-[hover=true]:bg-primary-50\
                          data-[selectable=true]:focus:bg-primary-50\
                            [&_[data-selected=true]]:!bg-primary-50\
                            [&_[aria-selected=true]]:!bg-primary-50\
                              [&_[data-selected=false]]:!bg-transparent\
                              [&_[aria-selected=false]]:!bg-transparent",
                        }}
                      >
                        {commissionTypeOptions.map((item) => (
                          <SelectItem key={item.key}>{item.label}</SelectItem>
                        ))}
                      </Select>

                      {/* Commission Structure - Per Side vs Round Trip */}
                      <Select
                        label="Commission Structure"
                        variant="underlined"
                        size="sm"
                        selectedKeys={[formData.commissionStructure]}
                        onSelectionChange={(keys) => 
                          updateFormData('commissionStructure', Array.from(keys)[0] as string)
                        }
                        description="Specify if commission is charged per side or round trip"
                        classNames={{
                          listboxWrapper: "data-[hover=true]:bg-primary-50\
                          data-[selectable=true]:focus:bg-primary-50\
                            [&_[data-selected=true]]:!bg-primary-50\
                            [&_[aria-selected=true]]:!bg-primary-50\
                              [&_[data-selected=false]]:!bg-transparent\
                              [&_[aria-selected=false]]:!bg-transparent",
                        }}
                      >
                        <SelectItem key="perSide">Per Side</SelectItem>
                        <SelectItem key="roundTrip">Round Trip</SelectItem>
                      </Select>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          label="Commission Amount"
                          variant="underlined"
                          size="sm"
                          type="number"
                          value={formData.commissionValue || ''}
                          onValueChange={(value) => updateFormData('commissionValue', value)}
                          isInvalid={!!errors['commissionValue']}
                          errorMessage={errors['commissionValue']}
                          {...getCommissionInputProps()}
                        />

                        {(formData.commissionType === 'fixed' || formData.commissionType === 'perContract') && (
                          <Input
                            label="Min Commission (Optional)"
                            variant="underlined"
                            size="sm"
                            type="number"
                            startContent={getCurrencySymbol(formData.commissionCurrency)}
                            placeholder="0.00"
                            step="0.01"
                            value={formData.commissionMinimum || ''}
                            onValueChange={(value) => updateFormData('commissionMinimum', value)}
                            isInvalid={!!errors['commissionMinimum']}
                            errorMessage={errors['commissionMinimum']}
                            description={`Minimum commission charge per ${formData.commissionStructure === 'perSide' ? 'side' : 'round trip'}`}
                          />
                        )}
                      </div>

                      <div className={formData.commissionType === 'percentage' ? 'opacity-50' : ''}>
                        {/* <label className="block text-sm font-medium text-foreground pb-1.5">
                          Currency
                        </label> */}
                        <p className="text-tiny text-default mb-1">
                          Commission Currency
                        </p>
                        <Button
                          variant="faded"
                          className="w-full justify-start"
                          onPress={onCurrencyChooserOpen}
                          isDisabled={formData.commissionType === 'percentage'}
                          startContent={
                            <span className="text-lg">{getCurrencySymbol(formData.commissionCurrency)}</span>
                          }
                        >
                          {formData.commissionCurrency}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Overnight Financing Configuration */}
              <Card radius="sm" shadow="none" className="border border-default-200 overflow-visible">
                <CardBody className="gap-4 overflow-visible">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-warning" />
                      <h4 className="font-semibold">Overnight Financing</h4>
                    </div>
                    <Switch
                      size="sm"
                      isSelected={formData.hasOvernightFinancing}
                      onValueChange={(value) => updateFormData('hasOvernightFinancing', value)}
                    />
                  </div>
                  
                  {formData.hasOvernightFinancing && (
                    <div className="space-y-4">
                      <Select
                        label="Benchmark Rate"
                        variant="underlined"
                        size="sm"
                        selectedKeys={[formData.overnightBenchmark]}
                        onSelectionChange={(keys) => 
                          updateFormData('overnightBenchmark', Array.from(keys)[0] as string)
                        }
                        classNames={{
                          listboxWrapper: "data-[hover=true]:bg-primary-50\
                          data-[selectable=true]:focus:bg-primary-50\
                            [&_[data-selected=true]]:!bg-primary-50\
                            [&_[aria-selected=true]]:!bg-primary-50\
                              [&_[data-selected=false]]:!bg-transparent\
                              [&_[aria-selected=false]]:!bg-transparent",
                        }}
                      >
                        {benchmarkOptions.map((item) => (
                          <SelectItem key={item.key}>{item.label}</SelectItem>
                        ))}
                      </Select>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          label="Admin Spread"
                          variant="underlined"
                          size="sm"
                          type="number"
                          endContent="%"
                          placeholder="2.5"
                          step="0.1"
                          value={formData.overnightSpread || ''}
                          onValueChange={(value) => updateFormData('overnightSpread', value)}
                          isInvalid={!!errors['overnightSpread']}
                          errorMessage={errors['overnightSpread']}
                          description="Spread above/below benchmark rate"
                        />
                        
                        <Select
                          label="Frequency"
                          variant="underlined"
                          size="sm"
                          selectedKeys={[formData.overnightFrequency]}
                          onSelectionChange={(keys) => 
                            updateFormData('overnightFrequency', Array.from(keys)[0] as string)
                          }
                          classNames={{
                            listboxWrapper: "data-[hover=true]:bg-primary-50\
                            data-[selectable=true]:focus:bg-primary-50\
                              [&_[data-selected=true]]:!bg-primary-50\
                              [&_[aria-selected=true]]:!bg-primary-50\
                                [&_[data-selected=false]]:!bg-transparent\
                                [&_[aria-selected=false]]:!bg-transparent",
                          }}
                        >
                          <SelectItem key="daily">Daily</SelectItem>
                          <SelectItem key="weekends">Weekends Only</SelectItem>
                          <SelectItem key="holidays">Holidays Only</SelectItem>
                        </Select>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Currency Conversion Configuration */}
              <Card radius="sm" shadow="none" className="border border-default-200 overflow-visible">
                <CardBody className="gap-4 overflow-visible">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-secondary" />
                      <h4 className="font-semibold">Currency Conversion</h4>
                    </div>
                    <Switch
                      size="sm"
                      isSelected={formData.hasCurrencyConversion}
                      onValueChange={(value) => updateFormData('hasCurrencyConversion', value)}
                    />
                  </div>
                  
                  {formData.hasCurrencyConversion && (
                    <div className="space-y-4">
                      <Select
                        label="Rate Type"
                        variant="underlined"
                        size="sm"
                        selectedKeys={[formData.conversionRateType]}
                        onSelectionChange={(keys) => 
                          updateFormData('conversionRateType', Array.from(keys)[0] as string)
                        }
                        classNames={{
                          listboxWrapper: "data-[hover=true]:bg-primary-50\
                          data-[selectable=true]:focus:bg-primary-50\
                            [&_[data-selected=true]]:!bg-primary-50\
                            [&_[aria-selected=true]]:!bg-primary-50\
                              [&_[data-selected=false]]:!bg-transparent\
                              [&_[aria-selected=false]]:!bg-transparent",
                        }}
                      >
                        <SelectItem key="spot">Spot Rate</SelectItem>
                        <SelectItem key="interbank">Interbank Rate</SelectItem>
                        <SelectItem key="spotPlusMarkup">Spot Rate + Markup</SelectItem>
                      </Select>
                      
                      {formData.conversionRateType === 'spotPlusMarkup' && (
                        <Input
                          label="Conversion Markup"
                          variant="underlined"
                          size="sm"
                          type="number"
                          endContent="%"
                          placeholder="0.3"
                          step="0.1"
                          value={formData.conversionMarkup || ''}
                          onValueChange={(value) => updateFormData('conversionMarkup', value)}
                          isInvalid={!!errors['conversionMarkup']}
                          errorMessage={errors['conversionMarkup']}
                          description="Markup percentage added to spot rate"
                        />
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Additional Notes */}
              <div>
                <Input
                  label="Additional Notes (Optional)"
                  variant='bordered'
                  placeholder="Any special fee conditions or notes..."
                  value={formData.notes || ''}
                  onValueChange={(value) => updateFormData('notes', value)}
                />
              </div>
            </ModalBody>

            {errors['general'] && (
              <div className="px-6 pb-4">
                <Alert 
                  color="danger" 
                  variant="flat"
                  title="Validation Error"
                  description={errors['general']}
                />
              </div>
            )}

            <ModalFooter>
              <Button 
                color="danger" 
                variant="light" 
                onPress={handleCancel}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                color="primary" 
                onPress={handleSave}
                isLoading={isSubmitting}
                startContent={!isSubmitting ? <DollarSign className="w-4 h-4" /> : undefined}
              >
                {isSubmitting ? 'Saving...' : 'Save Fees'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>

      {/* Currency Chooser Modal */}
      <CurrencyChooserModal
        isOpen={isCurrencyChooserOpen}
        onClose={onCurrencyChooserClose}
        onCurrencySelect={handleCurrencySelect}
      />
    </Modal>
  );
};